import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { EditPortalFormFieldValues, editPortalSchema } from '../logics/schema';
import { usePatchPortal } from './usePatchPortal';

type Args = {
  portalId: string;
  defaultValues: EditPortalFormFieldValues;
  defaultFileUrl?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

type IUseEditPortalModal = (args: Args) => {
  state: {
    methods: UseFormReturn<EditPortalFormFieldValues>;
    isMutating: boolean;
    selectedFile: File | undefined;
    existingFileUrl: string | undefined;
    hasFile: boolean;
  };
  actions: {
    handleSubmit: () => void;
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleRemoveFile: () => void;
  };
};

export const useEditPortalModal: IUseEditPortalModal = ({
  portalId,
  defaultValues,
  defaultFileUrl,
  onClose,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() || '';
  const id = Number.parseInt(portalId, 10);

  const methods = useForm<EditPortalFormFieldValues>({
    resolver: zodResolver(editPortalSchema),
    criteriaMode: 'all',
    defaultValues: {
      ...defaultValues,
      file: defaultFileUrl ?? null,
    },
  });

  const watchedFile = methods.watch('file');

  const hasFile = !!watchedFile;
  const selectedFile = watchedFile instanceof File ? watchedFile : undefined;
  const existingFileUrl = typeof watchedFile === 'string' ? watchedFile : undefined;

  const { trigger, isMutating } = usePatchPortal(threeLetterCode, id, {
    onSuccess: () => {
      showToast({ message: 'ポータルを更新しました。', status: 'success' });
      onSuccess?.();
      onClose();
    },
    onError: () => {
      showToast({ message: 'ポータルの更新に失敗しました。', status: 'danger' });
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      methods.setValue('file', file, { shouldValidate: true });
      methods.setValue('item_type', 'file', { shouldValidate: true });
    }
    event.target.value = '';
  };

  const handleRemoveFile = () => {
    methods.setValue('file', null, { shouldValidate: true });
  };

  const handleSubmit = methods.handleSubmit(async data => {
    const isKeepExistingFile = typeof data.file === 'string';

    await trigger(data, isKeepExistingFile);
  });

  return {
    state: {
      methods,
      isMutating,
      selectedFile,
      existingFileUrl,
      hasFile,
    },
    actions: { handleSubmit, handleFileChange, handleRemoveFile },
  };
};
